import { InMemoryWebStorage, SigninRedirectArgs, WebStorageStateStore } from 'oidc-client-ts';
import { Environment } from '../../common/types';
import { asBool, neverReachedFor } from '../../common/utils';
import { LoginConfig } from '../authority';
import { LogisticsTenant } from '../tokenHandling/tokenSessionValidation';

export const resolveEnvironment = (hostname: string) => {
    if (hostname.startsWith(Environment.VW_QA)) {
        return Environment.VW_QA;
    }
    if (hostname.startsWith(Environment.VW)) {
        return Environment.VW;
    }
    return Environment.RIO;
};

export const environmentForLogisticsTenant = (logisticsTenant: LogisticsTenant | undefined): Environment => {
    switch (logisticsTenant) {
        case LogisticsTenant.RIO:
            return Environment.RIO;
        case LogisticsTenant.VW_QA:
            return Environment.VW_QA;
        case LogisticsTenant.VW:
            return Environment.VW;
        case undefined:
            return Environment.RIO;
        default:
            return neverReachedFor(logisticsTenant);
    }
};

export const logisticsTenantMatchesEnvironment = (
    logisticsTenant: LogisticsTenant | undefined,
    environment: Environment | undefined,
): boolean => environmentForLogisticsTenant(logisticsTenant) === environment;

export const rioLoginConfig = (): LoginConfig => ({
    authority: import.meta.env.VITE_LOGIN_AUTHORITY,
    clientId: import.meta.env.VITE_CLIENT_ID,
    redirectUri: import.meta.env.VITE_LOGIN_REDIRECT_URI,
    silentRedirectUri: import.meta.env.VITE_LOGIN_SILENT_REDIRECT_URI,
    userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
    postLogoutRedirectUri: import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI,
    oauthScope: ['openid', 'profile', 'email'],
    preventRedirect: asBool(import.meta.env.VITE_LOGIN_PREVENT_REDIRECT),
    mockAuthorization: import.meta.env.DEV,
    mockLocale: import.meta.env.VITE_LOGIN_MOCK_LOCALE,
    mockTenant: import.meta.env.VITE_LOGIN_MOCK_TENANT,
});

export const createLoginConfig = (environment: Environment): LoginConfig => {
    switch (environment) {
        case Environment.RIO:
            return rioLoginConfig();
        case Environment.VW_QA:
            return {
                ...rioLoginConfig(),
                redirectUri: import.meta.env.VITE_LOGIN_REDIRECT_URI_VW_QA,
                silentRedirectUri: import.meta.env.VITE_LOGIN_SILENT_REDIRECT_URI_VW_QA,
                postLogoutRedirectUri: import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI_VW_QA,
            };
        case Environment.VW:
            return {
                ...rioLoginConfig(),
                redirectUri: import.meta.env.VITE_LOGIN_REDIRECT_URI_VW,
                silentRedirectUri: import.meta.env.VITE_LOGIN_SILENT_REDIRECT_URI_VW,
                postLogoutRedirectUri: import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI_VW,
            };
        default:
            throw new Error(`Identity provider not found: ${environment}`);
    }
};

export const getSigninOptions = (environment: Environment): SigninRedirectArgs => {
    switch (environment) {
        case Environment.VW_QA:
            return { extraQueryParams: { idp: 'cloud-vwgroup-com-qa' } };
        case Environment.VW:
            return { extraQueryParams: { idp: 'cloud-vwgroup-com' } };
        case Environment.RIO:
            return {};
        default:
            throw new Error(`Identity provider not found: ${environment}`);
    }
};
